import { utcDay } from 'd3-time';
import { entityTypeToApiType } from 'helpers/mapEntityTypes';
import { CompareModes, EntityTypes } from 'context/entityContext';
import { ITrendDataItem, IEntity } from '../types';
import { CountryIds, IIterator } from 'types/shared';

export interface INormalizedPeriods {
  breakdown?: string;
  breakdownCustomerId?: number;
  countryId?: CountryIds;
  data: (IPeriodData | ITrendDataItem)[];
  entity: IEntity;
  salesType?: string;
  totalAlbumsForPeriod?: number;
  totalTracksForPeriod?: number;
  totalForPeriod: number;
}

export interface IPeriodData extends IIterator {
  periodKey: Date;
  value: number;
}

export interface IPeriodsEntityData {
  customerId?: number;
  customerName?: string;
  data: (IPeriodData | ITrendDataItem)[];
  entity: IEntity;
  totalForPeriod: number;
  countryId?: CountryIds;
  salesType?: string;
}

interface IProps {
  entities: IPeriodsEntityData[];
  split?: EntityTypes;
  compareMode?: CompareModes;
  entityIds?: string[];
  countryIds?: string[];
  salesType?: string;
}
/**
 * This function normalizes the periodKey for each data point to a JavaScript date object.
 *
 * @export
 * @param {Object} args
 * @returns {Array} The same array of entities, with a date key added to each datapoint.
 */
export default function normalizePeriodsToDate(
  args: IProps
): INormalizedPeriods[] | any[] {
  const {
    entities,
    split,
    compareMode,
    entityIds,
    countryIds,
    salesType,
  } = args;

  if (!entities || !Array.isArray(entities)) return [];

  // Loop over the entities
  return (
    entities
      /** Filter the (old) data based on the latest compare state */
      .filter((item) => {
        if (compareMode === 'entity') {
          return entityIds?.includes(item.entity.id);
        } else if (compareMode === 'country') {
          return item.countryId && countryIds?.includes(item.countryId);
        }

        return true;
      })
      .map((entity) => {
        // Return a new entity object
        const nextEntity = {
          // Copy the existing entity
          ...entity,
          // Loop over the data array for that entity
          data: entity.data
            .map((d: IPeriodData | ITrendDataItem) => {
              /// Return a new data point object
              return {
                ...d,
                // Copy the existing data point
                value: split
                  ? d[`${entityTypeToApiType(split)}_value`]
                  : d.value,
                // Add a date key with a UTC based date
                date: utcDay.round(new Date(d.periodKey)),
              };
            })
            .sort(
              (
                dA: IPeriodData | ITrendDataItem,
                dB: IPeriodData | ITrendDataItem
              ) => dB.date - dA.date
            ),
        };

        if (salesType) {
          nextEntity.salesType = salesType;
        }

        return nextEntity;
      })
  );
}
